import type React from 'react';
import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';

export type UseCollapseOnOverflowOptions = {
  /**
   * Force collapsed state regardless of overflow (e.g., below a breakpoint)
   */
  forceCollapse?: boolean;
  /**
   * Also observe children for size changes (e.g., dynamic badges)
   */
  observeChildren?: boolean;
  /**
   * Optional external constraint element whose width should be used as the available width
   * for overflow calculation (e.g., the drawer container). If not provided, the measured
   * element's own clientWidth is used.
   */
  constraintRef?: React.RefObject<HTMLElement>;
  /**
   * When set, logs measurement details to console.debug under `[useCollapseOnOverflow:${label}]`
   * so you can compare runs (e.g. drawer ref timing, widths at first paint).
   */
  debugLabel?: string;
  /**
   * Optional convenience: auto-call `recheck()` when a record changes (e.g. drawer id) or when
   * the tab set changes.
   *
   * The signature is derived from `tabs[].label` (since labels drive width).
   */
  recheckOn?: {
    /** Include a stable id to recheck when switching records (e.g. connectionId) */
    id?: unknown;
    /** Tabs to derive a signature from */
    tabs?: readonly { label: string }[];
  };
};

export type UseCollapseOnOverflowReturn<T extends HTMLElement> = {
  /** Attach to the container that wraps the tabs list */
  ref: React.RefObject<T>;
  /** True when content overflows or when forceCollapse is set */
  collapsed: boolean;
  /** Manually trigger a re-check if needed */
  recheck: () => void;
};

/** Content width inside horizontal padding on `el`, or clientWidth if parsing fails */
function constraintContentBoxWidthPx(constraintEl: HTMLElement): number {
  try {
    const styles = window.getComputedStyle(constraintEl);
    const paddingLeft = parseFloat(styles.paddingLeft || '0') || 0;
    const paddingRight = parseFloat(styles.paddingRight || '0') || 0;
    return Math.max(0, constraintEl.clientWidth - paddingLeft - paddingRight);
  } catch {
    return constraintEl.clientWidth;
  }
}

function logCollapseMeasure(
  logPrefix: string,
  logSeqRef: MutableRefObject<number>,
  reason: string,
  options: UseCollapseOnOverflowOptions,
  el: HTMLElement,
  constraintEl: HTMLElement | null,
  constraintContentWidth: number | null,
  selfWidth: number,
  availableWidth: number,
  contentWidth: number,
  hasOverflow: boolean
) {
  const seq = ++logSeqRef.current;
  console.debug(`${logPrefix} measure`, {
    seq,
    reason,
    hasOverflow,
    collapsed: !!(options.forceCollapse || hasOverflow),
    forceCollapse: !!options.forceCollapse,
    widths: {
      constraintContentWidth,
      selfWidth,
      availableWidth,
      contentWidth,
      delta: contentWidth - availableWidth,
    },
    el: {
      tag: el.tagName,
      connected: typeof el.isConnected === 'boolean' ? el.isConnected : undefined,
      clientWidth: el.clientWidth,
      scrollWidth: el.scrollWidth,
      offsetParent: el.offsetParent ? (el.offsetParent as HTMLElement).tagName : null,
    },
    constraint: constraintEl
      ? {
          tag: constraintEl.tagName,
          connected:
            typeof constraintEl.isConnected === 'boolean' ? constraintEl.isConnected : undefined,
          clientWidth: constraintEl.clientWidth,
        }
      : null,
  });
}

/** Detect ResizeObserver without `any`; undefined in SSR or very old browsers */
function resizeObserverCtor(): typeof ResizeObserver | undefined {
  if (typeof ResizeObserver !== 'undefined') return ResizeObserver;
  return undefined;
}

type OverflowMeasureInput = Readonly<{
  measuredEl: HTMLElement;
  constraintRef?: React.RefObject<HTMLElement>;
}>;

/** Pure width math shared by checkOverflow logic */
function measureHorizontalOverflow(opts: OverflowMeasureInput) {
  const constraintEl = opts.constraintRef?.current as HTMLElement | null | undefined;
  const constraintContentWidth =
    constraintEl != null ? constraintContentBoxWidthPx(constraintEl) : null;

  const { measuredEl } = opts;
  const selfWidth = measuredEl.clientWidth;
  const availableWidth = Math.min(constraintContentWidth ?? Number.POSITIVE_INFINITY, selfWidth);
  const contentWidth = measuredEl.scrollWidth;
  const hasOverflow = contentWidth > availableWidth;

  return {
    constraintEl,
    constraintContentWidth,
    selfWidth,
    availableWidth,
    contentWidth,
    hasOverflow,
  };
}

function tabsSignature(tabs: readonly { label: string }[] | undefined): string {
  if (!tabs || tabs.length === 0) return '';
  const parts = tabs.map((t) => t.label);
  return parts.join('|');
}

/**
 * Detects when the horizontal content of a container overflows its width and provides a
 * `collapsed` boolean you can use to switch UI (e.g., tabs → dropdown).
 *
 * This hook is commonly used in drawers:
 * - Render a `TabsList` when `collapsed === false`
 * - Render a `<DropdownSelect>` when `collapsed === true`
 * - Set `forceCollapse: isMobile` if you want mobile to always use the dropdown
 * - Pass `constraintRef` to measure against the drawer’s content width (recommended)
 *
 * Important: keep the measured element mounted
 * - Attach `ref` to an element that stays mounted even when you render the dropdown.
 * - If you unmount the measured node when `collapsed` becomes true, the hook can’t re-measure
 *   to recover (you’ll see repeated “skip (no ref)” logs when debug is enabled).
 * - Preferred pattern: keep the tabs DOM mounted but visually hidden (`invisible`, `max-h-0`,
 *   `overflow-hidden`, `pointer-events-none`) while collapsed.
 *
 * Recommended: re-measure when the tab set changes
 * - If your drawer’s tabs are dynamic (e.g., conditionally adding a tab), call `recheck()` when
 *   the record id or tab codes change.
 *
 * @example
 * ```tsx
 * import { useEffect, useMemo, useRef, useState } from 'react';
 * import type { CodeDefinitionOption } from '../components/dropdown-select';
 * import { DropdownSelect } from '../components/dropdown-select';
 * import { Tabs, TabsButton, TabsContent, TabsList } from '../components/tabs';
 * import { useCollapseOnOverflow } from '../hooks/useCollapseOnOverflow';
 * import { useIsMobile } from '../hooks/useIsMobile';
 *
 * export function DrawerWithResponsiveTabs({ recordId }: { recordId: string }) {
 *   const isMobile = useIsMobile();
 *   const drawerRef = useRef<HTMLDivElement>(null);
 *
 *   const tabs = useMemo(
 *     () => [
 *       { code: 'details', label: 'Details' as const },
 *       { code: 'documents', label: 'Documents' as const },
 *     ],
 *     []
 *   );
 *
 *   const [activeTab, setActiveTab] = useState<string>('details');
 *   useEffect(() => setActiveTab('details'), [recordId]);
 *
 *   const { ref: tabsMeasureRef, collapsed, recheck } = useCollapseOnOverflow<HTMLDivElement>({
 *     observeChildren: true,
 *     forceCollapse: isMobile, // mobile always dropdown
 *     constraintRef: drawerRef, // measure against drawer width
 *     recheckOn: { id: recordId, tabs }, // optional convenience
 *   });
 *
 *   return (
 *     <div ref={drawerRef}>
 *       <Tabs activeTab={activeTab} onChange={setActiveTab}>
 *         {collapsed ? (
 *           <DropdownSelect
 *             inputId="drawer-section"
 *             aria-label="Drawer section"
 *             allOptions={tabs.map((t) => ({ value: t.code, label: t.label }))}
 *             value={activeTab}
 *             isSearchable={false}
 *             isClearable={false}
 *             onChange={(opt: CodeDefinitionOption | CodeDefinitionOption[] | null) => {
 *               if (opt && !Array.isArray(opt)) setActiveTab(opt.value);
 *             }}
 *           />
 *         ) : null}
 *
 *         // Keep the measured element mounted even when collapsed (hide instead of unmount).
 *         <div
 *           ref={tabsMeasureRef}
 *           aria-hidden={collapsed}
 *           className={
 *             collapsed
 *               ? 'tw:invisible tw:max-h-0 tw:overflow-hidden tw:pointer-events-none'
 *               : undefined
 *           }
 *         >
 *           <TabsList>
 *             {tabs.map((t) => (
 *               <TabsButton key={t.code} tab={t.code}>
 *                 {t.label}
 *               </TabsButton>
 *             ))}
 *           </TabsList>
 *         </div>
 *
 *         <TabsContent tab="details">...</TabsContent>
 *         <TabsContent tab="documents">...</TabsContent>
 *       </Tabs>
 *     </div>
 *   );
 * }
 * ```
 *
 * SSR safe. Uses `ResizeObserver` when available and falls back to window resize listeners.
 */
export function useCollapseOnOverflow<T extends HTMLElement>(
  options: UseCollapseOnOverflowOptions = {}
): UseCollapseOnOverflowReturn<T> {
  const containerRef = useRef<T>(null);
  const [overflowing, setOverflowing] = useState(false);
  const logPrefix = options.debugLabel ? `[useCollapseOnOverflow:${options.debugLabel}]` : null;
  /** Monotonic-ish id so console logs sort in order per hook instance */
  const logSeqRef = useRef(0);

  const checkOverflow = (reason: string) => {
    const el = containerRef.current as HTMLElement | null;
    if (!el) {
      if (logPrefix) {
        console.debug(`${logPrefix} skip (no ref)`, { reason });
      }
      return;
    }

    const m = measureHorizontalOverflow({ measuredEl: el, constraintRef: options.constraintRef });
    if (logPrefix) {
      logCollapseMeasure(
        logPrefix,
        logSeqRef,
        reason,
        options,
        el,
        m.constraintEl ?? null,
        m.constraintContentWidth,
        m.selfWidth,
        m.availableWidth,
        m.contentWidth,
        m.hasOverflow
      );
    }
    setOverflowing(m.hasOverflow);
  };

  const checkOverflowRef = useRef(checkOverflow);
  checkOverflowRef.current = checkOverflow;

  const recheck = useCallback(() => {
    checkOverflowRef.current('recheck-manual');
  }, []);

  const recheckSignature = tabsSignature(options.recheckOn?.tabs);

  useEffect(() => {
    return setupOverflowObservers(options, containerRef, checkOverflow, logPrefix);
    // Intentionally narrow deps: full `options` / `checkOverflow` change every render when the
    // parent passes a new inline options object; `constraintRef?.current` tracks drawer attach.
    // `recheckOn.id` + the tab signature re-subscribe when the measured element mounts LATER
    // than this hook's first run (a drawer whose tabs render only once its query resolves):
    // without them the observers are never installed unless some unrelated re-render happens
    // to fire after the ref attaches. Callers flip `recheckOn.id` once data is present.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    options.observeChildren,
    options.forceCollapse,
    options.debugLabel,
    options.constraintRef?.current,
    options.recheckOn?.id,
    recheckSignature,
  ]);

  useEffect(() => {
    if (!options.recheckOn) return;
    recheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.recheckOn?.id, recheckSignature, recheck]);

  const collapsed = options.forceCollapse || overflowing;

  return {
    ref: containerRef,
    collapsed,
    recheck,
  };
}

/** Measure when ref is absent (component first paint before `<nav>` mounts) vs attached */
function setupOverflowObservers<T extends HTMLElement>(
  options: UseCollapseOnOverflowOptions,
  containerRef: React.RefObject<T | null>,
  checkOverflow: (reason: string) => void,
  logPrefix: string | null
) {
  const el = containerRef.current as HTMLElement | null;
  if (!el) {
    if (logPrefix) {
      console.debug(`${logPrefix} effect:no-target (ref not mounted yet?)`, {
        constraintAttached: !!(options.constraintRef?.current as HTMLElement | null),
      });
    }
    return;
  }
  if (typeof window === 'undefined') return;

  const RO = resizeObserverCtor();
  if (logPrefix) {
    console.debug(`${logPrefix} effect:subscribe`, {
      observeChildren: !!options.observeChildren,
      resizeObserverAvailable: !!RO,
      constraintAttached: !!(options.constraintRef?.current as HTMLElement | null),
    });
  }

  let raf = 0;
  const onResize = () => {
    cancelAnimationFrame(raf);
    raf = window.requestAnimationFrame(() => checkOverflow('resize-or-raf'));
  };

  checkOverflow('effect-initial');

  let ro: ResizeObserver | null = null;
  if (RO) {
    ro = new RO(onResize);
    ro.observe(el);
    if (options.constraintRef?.current) {
      ro.observe(options.constraintRef.current);
    }
    if (options.observeChildren) {
      Array.from(el.children).forEach((child) => ro!.observe(child as Element));
    }
  }

  window.addEventListener('resize', onResize);
  window.addEventListener('orientationchange', onResize);

  return () => {
    if (logPrefix) {
      console.debug(`${logPrefix} effect:cleanup`);
    }
    cancelAnimationFrame(raf);
    if (ro) ro.disconnect();
    window.removeEventListener('resize', onResize);
    window.removeEventListener('orientationchange', onResize);
  };
}
